import { UserStore } from '../storage/user-storage-manager.ts'
import { EventTracker } from '@core/events/event-manager.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { delay } from '@core/utils/delay.ts'
import { SessionConfig } from '@core/types/session-config.ts'
import { Logger, TaggedLogger } from '@core/logging/logger.ts'
import { SessionStore } from '../storage/session-storage-manager.ts'
import { Env } from '../env.ts'
import { throttle } from '../utils/throttle.ts'
import { getUuidV7 } from '@core/utils/uuid.ts'
import { TrackedEventUtils } from '@core/events/tracked-event.ts'

/** @internal */
export class HeartbeatManager {
    static readonly DEFAULT_INACTIVITY_TIMEOUT_SECONDS = 30 * 60
    static readonly HEARTBEAT_INTERVAL_TIME = 60 * 1000

    protected heartbeatInterval: NodeJS.Timeout | null

    protected readonly inactivityTimeout: number
    private readonly _logger: Logger

    private sessionStartThrottle: () => void = throttle(async () => await this._checkAndStartSession(), 100)

    /**
     * Additional callbacks driven by the single shared passive `scroll` listener registered in
     * {@link HeartbeatManager#_initializeEventListeners}. Subscribers (e.g. the
     * {@link ScrollDepthObserver}) are notified on every scroll event — with the Event, so they
     * can read `event.target` — without registering a second `scroll` listener (one passive
     * listener, no second listener).
     */
    private readonly scrollSubscribers: Array<(ev?: Event) => void> = []

    /**
     * Guards `sdk_impression_end` to at most once per session. Reset only in {@link _startNewSession}
     * so a resume within the inactivity timeout keeps the same session_id and does not re-arm it.
     */
    private sdkImpressionEndSent = false

    private isStopped = false

    constructor(
        private readonly eventManager: EventTracker,
        private readonly userStore: UserStore,
        private readonly sessionStore: SessionStore,
        config: SessionConfig | null,
        /**
         * Predicate for the `SDK_DEBUG_EVENTS` domain flag (e.g. `DebugController._getIsEventsEnabled`).
         * Gates the non-subscriber `sdk_impression_end` diagnostic. Session lifecycle events
         * (`session_start`/`session_heartbeat`) are subscribed-only and are not widened by this flag.
         */
        private readonly debugEnabled: () => Promise<boolean> = () => Promise.resolve(false),
        logger?: Logger | null,
    ) {
        const configIntervalSeconds = config?.threshold?.intervalSeconds

        this._logger = logger ?? TaggedLogger._getInstance('HeartbeatManager')
        this.heartbeatInterval = null
        this.inactivityTimeout = (configIntervalSeconds || HeartbeatManager.DEFAULT_INACTIVITY_TIMEOUT_SECONDS) * 1000

        this._initializeEventListeners()
    }

    public get _window(): Window {
        return Env._scope
    }

    public get _document(): Document {
        return Env._document
    }

    public async onSubscriptionChanged(granted: boolean) {
        if (granted) {
            await this._checkAndStartSession()
        } else {
            this._stopHeartbeat()
        }
    }

    /**
     * Stops the heartbeat for the rest of this page. Activity, focus and visibility changes no
     * longer restart it.
     */
    public _stop(): void {
        this.isStopped = true
        this._stopHeartbeat()
    }

    protected _initializeEventListeners(): void {
        this._document.addEventListener('visibilitychange', () => this._handleVisibilityChange(!this._document.hidden))
        window.addEventListener('focus', this._handleFocus.bind(this))
        window.addEventListener('blur', this._handleBlur.bind(this))
        // `pagehide` is the most reliable terminal signal (incl. bfcache / mobile) as a backstop for
        // paths where visibilitychange:hidden does not fire before unload.
        this._window.addEventListener('pagehide', () => void this._maybeSendSdkImpressionEnd('pagehide'))
        // Track non-scroll user activity.
        const events: string[] = ['mousedown', 'keydown', 'mousemove', 'touchstart']
        events.forEach((eventName) => {
            this._document.addEventListener(eventName, this._handleUserActivity.bind(this))
        })
        // Scroll does not bubble, so capture is required to observe inner containers. The shared
        // listener both forwards to subscribers and makes in-container scrolling count as activity.
        this._document.addEventListener('scroll', this._handleScroll.bind(this), { passive: true, capture: true })
    }

    /**
     * Registers an additional callback to be invoked on each scroll event from the single shared
     * passive `scroll` listener. Used by the {@link ScrollDepthObserver} so scroll-depth tracking
     * reuses this one listener instead of registering its own. The Event is forwarded so
     * subscribers can identify the scrolled target.
     */
    public _subscribeToScroll(callback: (ev?: Event) => void): void {
        this.scrollSubscribers.push(callback)
    }

    private _handleScroll(ev?: Event): void {
        void this._handleUserActivity()
        for (const subscriber of this.scrollSubscribers) {
            try {
                subscriber(ev)
            } catch (error) {
                this._logger.verbose('Scroll subscriber failed', error)
            }
        }
    }

    protected _handleVisibilityChange(visible: boolean) {
        if (visible) {
            this.sessionStartThrottle()
        } else {
            void this._maybeSendSdkImpressionEnd('hidden')
            this._stopHeartbeat()
        }
    }

    /**
     * Fires the debug-only `sdk_impression_end` diagnostic at most once per session, on the first
     * `visibilitychange → hidden` (with `pagehide` as a terminal backstop). It pairs with the
     * debug-gated `sdk_impression` to measure early exits by NON-subscribers, so it is emitted only
     * when the user is not subscribed AND debug is enabled. Sends via the normal immediate/keepalive
     * pipeline (no sendBeacon). Payload: top-level `session_id` (stamped by `_build`) +
     * `{ reason, time_on_page_ms }`.
     */
    protected async _maybeSendSdkImpressionEnd(reason: 'hidden' | 'pagehide'): Promise<void> {
        if (this.sdkImpressionEndSent) return
        // Claim the once-per-session slot synchronously, BEFORE the async checks below, on purpose:
        // if two `visibilitychange → hidden` events (or a hidden→pagehide pair) fire back to back,
        // the second call re-enters this method while the first call's promises are still pending.
        // Setting the flag synchronously means the second call sees it already claimed and bails out
        // immediately, instead of both calls racing to send sdk_impression_end twice.
        this.sdkImpressionEndSent = true

        // Subscribers already emit session lifecycle events; this diagnostic only covers non-subscribers.
        if (await this.userStore._getIsSubscribed()) {
            return
        }

        if (!(await this.debugEnabled())) {
            return
        }

        const timeOnPageMs = await this._getTimeOnPageMs()
        this.eventManager._track(TrackedEventAction.SDK_IMPRESSION_END, {
            reason,
            time_on_page_ms: timeOnPageMs,
        })
        this._logger.verbose(`Sending sdk_impression_end event (${reason})`)
    }

    /**
     * Milliseconds elapsed since the current page-view started, mirroring how the prompt
     * `time_on_page_seconds` condition is measured (see PromptController). 0 when unknown.
     */
    protected async _getTimeOnPageMs(): Promise<number> {
        const start = await this.sessionStore._getCurrentPageViewStart()
        if (!start) {
            return 0
        }
        return Math.max(0, Date.now() - start.getTime())
    }

    private _handleFocus() {
        this.sessionStartThrottle()
    }

    private _handleBlur(): void {
        this._stopHeartbeat()
    }

    private async _handleUserActivity() {
        if (!this._document.hasFocus()) return
        const isSubscribed = await this.userStore._getIsSubscribed()
        if (isSubscribed) {
            await this._checkAndStartSession()
        }
    }

    protected async _checkAndStartSession() {
        if (this.isStopped) {
            return
        }

        const isSubscribed = await this.userStore._getIsSubscribed()

        // Session lifecycle events are subscribed-only; non-subscribers get no
        // session_start/session_heartbeat.
        if (!isSubscribed) return

        const currentDate = await this.getDateString()
        const lastActivityDate = await this.sessionStore._getLastActivity()
        const timeSinceLastActivity = Date.now() - (lastActivityDate?.getTime() || 0)
        const lastSessionDate = await this.sessionStore._getHeartbeatSessionDate()
        const lastSessionDateStr = lastSessionDate != null ? await this.getDateString(lastSessionDate) : null

        if (timeSinceLastActivity >= this.inactivityTimeout || currentDate !== lastSessionDateStr) {
            await this._startNewSession()
            // done through promise instead of await for test suite purposes
            await delay(HeartbeatManager.HEARTBEAT_INTERVAL_TIME)
            await this._startHeartbeat()
        } else {
            await this._startHeartbeat()
        }
    }

    protected async _startNewSession() {
        // Rotate the per-tab session_id first so every event tracked for this new session
        // (starting with SESSION_START) carries the fresh join key. Quick tab-switches take the
        // continue branch of _checkAndStartSession and never reach here, so the id stays stable.
        await this._rotateSessionId()
        // Re-arm the once-per-session early-exit signal for the fresh session_id.
        this.sdkImpressionEndSent = false
        await this.sessionStore._setHeartbeatSessionDate(new Date())
        await this.sessionStore._setLastActivity(new Date())
        this.eventManager._track(TrackedEventAction.SESSION_START)
        this._logger.verbose('Starting new heartbeat session')
    }

    protected async _rotateSessionId(): Promise<void> {
        const id = getUuidV7()
        await this.sessionStore._setSessionId(id)
        TrackedEventUtils._sessionId = id
    }

    protected async _startHeartbeat() {
        if (this.isStopped) {
            return
        }

        if (this.heartbeatInterval) return

        const isSubscribed = await this.userStore._getIsSubscribed()
        // Subscribed-only; heartbeats never fire for non-subscribers.
        const enabled = isSubscribed
        this._logger.verbose('Starting heartbeat session')
        this.heartbeatInterval = setInterval(() => {
            if (this._document.hasFocus() && enabled) {
                this.eventManager._track(TrackedEventAction.SESSION_HEARTBEAT)
                this.sessionStore._setLastActivity(new Date())
                this._logger.verbose('Sending session heartbeat event')
            } else {
                this._stopHeartbeat()
            }
        }, HeartbeatManager.HEARTBEAT_INTERVAL_TIME)
    }

    protected _stopHeartbeat(): void {
        if (this.heartbeatInterval) {
            this._logger.verbose('Stopping heartbeat session')
            clearInterval(this.heartbeatInterval)
            this.heartbeatInterval = null
        }
    }

    protected async getDateString(date: Date = new Date()) {
        const timezone = await this.userStore._getTimeZone()
        return date.toLocaleDateString('en-US', { timeZone: timezone || undefined })
    }
}
